import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  Card, 
  Descriptions, 
  Button, 
  Space, 
  Typography, 
  Tag, 
  Progress,
  Alert,
  Spin,
  Table,
  Tabs,
  Row,
  Col,
  Statistic,
  Tooltip,
  notification,
  Input,
  Select,
  Checkbox,
  Divider
} from 'antd';
import { 
  ArrowLeftOutlined,
  DownloadOutlined, 
  ReloadOutlined,
  PlayCircleOutlined,
  UserOutlined,
  SmileOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  SearchOutlined,
  FilterOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { videoApi } from '../services/api';
import socketService from '../services/socket';
import { getString } from '../utils/translations';
import dayjs from 'dayjs';
import { formatTimestamp, calculateDuration } from '../utils/dateUtils';
import { getVaProExportFilename } from '../utils/exportFilenames';
import ProgressDisplay from './ProgressDisplay';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const JobDetails = ({ socketConnected }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [results, setResults] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  // Results interface state
  const [selectedResults, setSelectedResults] = useState([]);
  const [resultsFilter, setResultsFilter] = useState({
    search: '',
    emotions: [],
    minFaces: null,
    maxFaces: null
  });
  const [filteredResults, setFilteredResults] = useState([]);

  // Fetch job status
  const { 
    data: jobData, 
    isLoading: jobLoading, 
    error: jobError,
    refetch: refetchJob
  } = useQuery(['job', jobId], () => videoApi.getJobStatus(jobId), {
    refetchInterval: socketConnected ? false : 10000,
    onSuccess: (data) => {
      setJob(data);
    }
  });

  // Fetch job results - COMMENTED OUT for future implementation
  // TODO: Re-enable when results view is restored
  /*
  const { 
    data: resultsData, 
    isLoading: resultsLoading, 
    error: resultsError,
    refetch: refetchResults
  } = useQuery(['jobResults', jobId], () => videoApi.getJobResults(jobId), {
    enabled: job?.status === 'completed',
    onSuccess: (data) => {
      setResults(data.results || []);
      setFilteredResults(data.results || []);
    }
  });
  */

  // Filter results based on search and filters - COMMENTED OUT for future implementation
  // TODO: Re-enable when results view is restored
  /*
  useEffect(() => {
    let filtered = [...results];

    // Search filter
    if (resultsFilter.search) {
      const searchLower = resultsFilter.search.toLowerCase();
      filtered = filtered.filter(result =>
        result.frame_idx?.toString().includes(searchLower) ||
        result.solo_results?.faces?.[0]?.expressions && 
        Object.keys(result.solo_results.faces[0].expressions).some(emotion => 
          emotion.toLowerCase().includes(searchLower)
        )
      );
    }

    // Faces count filter
    if (resultsFilter.minFaces !== null) {
      filtered = filtered.filter(result => 
        (result.solo_results?.faces?.length || 0) >= resultsFilter.minFaces
      );
    }
    if (resultsFilter.maxFaces !== null) {
      filtered = filtered.filter(result => 
        (result.solo_results?.faces?.length || 0) <= resultsFilter.maxFaces
      );
    }

    // Emotion filter
    if (resultsFilter.emotions.length > 0) {
      filtered = filtered.filter(result => {
        const expressions = result.solo_results?.faces?.[0]?.expressions;
        if (!expressions) return false;
        return resultsFilter.emotions.some(emotion => 
          expressions[emotion] && expressions[emotion] > 0.5
        );
      });
    }

    setFilteredResults(filtered);
  }, [results, resultsFilter]);
  */

  // Subscribe to real-time updates
  useEffect(() => {
    if (socketConnected && job && (job.status === 'processing' || job.status === 'uploading')) {
      socketService.subscribeToJob(jobId, handleJobUpdate);
      
      return () => {
        socketService.unsubscribeFromJob(jobId);
      };
    }
  }, [socketConnected, job, jobId]);

  // Handle real-time job updates
  const handleJobUpdate = (progressData) => {
    setJob(prevJob => ({
      ...prevJob,
      status: progressData.status || prevJob.status,
      progress: progressData.progress || prevJob.progress,
      processed_frames: progressData.processed_frames || prevJob.processed_frames,
      total_frames: progressData.total_frames || prevJob.total_frames,
      updated_at: new Date().toISOString()
    }));

    if (progressData.status === 'completed') {
      refetchResults();
      notification.success({
        message: getString('pipeline.details.processing_complete', 'Processing Complete'),
        description: getString('pipeline.details.processing_complete', 'Video analysis has been completed successfully.'),
        duration: 5,
      });
    }
  };

  // Download results
  const handleDownload = async () => {
    try {
      const blob = await videoApi.downloadResults(jobId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${job.filename}_results.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      notification.success({
        message: getString('pipeline.details.download_started', 'Download Started'),
        description: getString('pipeline.details.download_started', 'Results file download has started.'),
        duration: 3,
      });
    } catch (error) {
      notification.error({
        message: getString('pipeline.details.download_failed', 'Download Failed'),
        description: error.response?.data?.error || getString('pipeline.details.download_failed', 'Failed to download results'),
        duration: 5,
      });
    }
  };

  // Export results as CSV with all blendshapes (server default)
  const handleExport = async (format = 'csv') => {
    setExportLoading(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const exportResult = await videoApi.exportResults(jobId, { format, tz });
      if (exportResult.kind === 'queued') {
        notification.info({
          message: 'Large Export Queued',
          description: 'The CSV is being prepared. Download it from Recent Exports on the Job Dashboard when ready.',
          duration: 7,
        });
        return;
      }

      const blob = exportResult.blob;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const extension = format === 'json' ? 'json' : 'csv';
      const baseName = job.filename.replace(/\.[^/.]+$/, '') || job.filename || 'video';
      link.download = format === 'csv' ? getVaProExportFilename(job.filename) : `${baseName}_export.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      notification.success({
        message: getString('pipeline.details.export_started', 'Export Started'),
        description: `${format.toUpperCase()} export with all blendshapes has started.`,
        duration: 3,
      });
    } catch (error) {
      notification.error({
        message: getString('pipeline.details.export_failed', 'Export Failed'),
        description: error.response?.data?.error || getString('pipeline.details.export_failed', 'Failed to export results'),
        duration: 5,
      });
    } finally {
      setExportLoading(false);
    }
  };



  // Get status display
  const getStatusDisplay = (status) => {
    const statusConfig = {
      uploading: { color: 'blue', icon: <ClockCircleOutlined />, text: getString('pipeline.jobs.status_uploading', 'Uploading') },
      processing: { color: 'orange', icon: <ClockCircleOutlined />, text: getString('pipeline.jobs.processing', 'Processing') },
      completed: { color: 'green', icon: <CheckCircleOutlined />, text: getString('pipeline.jobs.completed', 'Completed') },
      error: { color: 'red', icon: <ExclamationCircleOutlined />, text: getString('pipeline.jobs.failed', 'Failed') }
    };
    
    return statusConfig[status] || statusConfig.error;
  };

  // Prepare results table data with filtering support
  const prepareTableData = () => {
    return filteredResults.map((result, index) => ({
      key: `${result.frame_idx}_${index}`,
      frame_idx: result.frame_idx,
      solo_faces: result.solo_results?.faces?.length || 0,
      solo_emotions: result.solo_results?.faces?.[0]?.expressions ? 
        Object.entries(result.solo_results.faces[0].expressions)
          .filter(([_, value]) => value > 0.3)
          .map(([emotion, value]) => `${emotion}(${(value * 100).toFixed(0)}%)`)
          .join(', ') : 'N/A',
      dominant_emotion: result.solo_results?.faces?.[0]?.expressions ? 
        Object.entries(result.solo_results.faces[0].expressions)
          .reduce((max, [emotion, value]) => value > max.value ? {emotion, value} : max, {emotion: 'none', value: 0})
          .emotion : 'N/A',
      facemesh_landmarks: result.facemesh_results?.landmarks?.length || 0,
      timestamp: result.created_at,
      result_data: result // Store full result for potential future use
    }));
  };

  // Enhanced table columns for data analyst workflow
  const columns = [
    {
      title: 'Frame',
      dataIndex: 'frame_idx',
      key: 'frame_idx',
      width: 80,
      sorter: (a, b) => a.frame_idx - b.frame_idx,
      fixed: 'left',
    },
    {
      title: 'Faces',
      dataIndex: 'solo_faces',
      key: 'solo_faces',
      width: 80,
      sorter: (a, b) => a.solo_faces - b.solo_faces,
      render: (count) => (
        <Space>
          <UserOutlined style={{ color: count > 0 ? '#52c41a' : '#d9d9d9' }} />
          <Text strong={count > 0}>{count}</Text>
        </Space>
      ),
    },
    {
      title: 'Dominant Emotion',
      dataIndex: 'dominant_emotion',
      key: 'dominant_emotion',
      width: 120,
      filters: [
        { text: 'Happy', value: 'happy' },
        { text: 'Sad', value: 'sad' },
        { text: 'Angry', value: 'angry' },
        { text: 'Surprised', value: 'surprised' },
        { text: 'Neutral', value: 'neutral' },
        { text: 'Fearful', value: 'fearful' },
        { text: 'Disgusted', value: 'disgusted' },
      ],
      onFilter: (value, record) => record.dominant_emotion === value,
      render: (emotion) => (
        <Tag color={
          emotion === 'happy' ? 'green' :
          emotion === 'sad' ? 'blue' :
          emotion === 'angry' ? 'red' :
          emotion === 'surprised' ? 'orange' :
          emotion === 'neutral' ? 'default' :
          emotion === 'fearful' ? 'purple' :
          emotion === 'disgusted' ? 'magenta' : 'default'
        }>
          {emotion === 'N/A' ? 'No Data' : emotion}
        </Tag>
      ),
    },
    {
      title: 'All Emotions (%)',
      dataIndex: 'solo_emotions',
      key: 'solo_emotions',
      width: 250,
      render: (emotions) => (
        <Tooltip title={emotions} placement="topLeft">
          <Text ellipsis style={{ maxWidth: 240, fontSize: '12px' }}>
            {emotions === 'N/A' ? 'No emotion data' : emotions}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Landmarks',
      dataIndex: 'facemesh_landmarks',
      key: 'facemesh_landmarks',
      width: 100,
      sorter: (a, b) => a.facemesh_landmarks - b.facemesh_landmarks,
      render: (count) => (
        <Text type={count > 0 ? 'success' : 'secondary'}>
          {count > 0 ? `${count} pts` : 'None'}
        </Text>
      ),
    },
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 100,
      render: (timestamp) => dayjs(timestamp).format('HH:mm:ss'),
    },
  ];

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys: selectedResults,
    onChange: (selectedRowKeys) => {
      setSelectedResults(selectedRowKeys);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      if (selected) {
        setSelectedResults(filteredResults.map((_, index) => `${filteredResults[index].frame_idx}_${index}`));
      } else {
        setSelectedResults([]);
      }
    },
  };

  if (jobError) {
    return (
      <Alert
        message={getString('pipeline.jobs.failed_to_load', 'Failed to Load Job')}
        description={jobError.response?.data?.error || jobError.message}
        type="error"
        showIcon
        action={
          <Space>
            <Button size="small" onClick={() => refetchJob()}>
              {getString('pipeline.jobs.refresh', 'Retry')}
            </Button>
            <Button size="small" onClick={() => navigate('/jobs')}>
              {getString('pipeline.details.back_to_dashboard', 'Back to Dashboard')}
            </Button>
          </Space>
        }
      />
    );
  }

  if (jobLoading || !job) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <Spin size="large" />
        <div style={{ marginTop: '1rem' }}>
          <Text type="secondary">{getString('pipeline.jobs.loading', 'Loading job details...')}</Text>
        </div>
      </div>
    );
  }

  const statusDisplay = getStatusDisplay(job.status);
  const isProcessing = job.status === 'processing' || job.status === 'uploading';
  const isCompleted = job.status === 'completed';
  const tableData = prepareTableData();

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <Space align="center" style={{ marginBottom: '1rem' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/jobs')}
          >
            {getString('pipeline.details.back_to_dashboard', 'Back to Dashboard')}
          </Button>
          <Title level={2} style={{ margin: 0 }}>{getString('pipeline.details.job_details_title', 'Job Details')}</Title>
          <Tag icon={statusDisplay.icon} color={statusDisplay.color} style={{ fontSize: '14px' }}>
            {statusDisplay.text}
          </Tag>
        </Space>

      </div>

      {/* Job Information */}
      <Row gutter={16} style={{ marginBottom: '2rem' }}>
        <Col span={18}>
          <Card title={getString('pipeline.details.job_info', 'Job Information')}>
            <Descriptions column={2}>
              <Descriptions.Item label={getString('pipeline.details.job_id', 'Job ID')}>{job.job_id}</Descriptions.Item>
              <Descriptions.Item label={getString('pipeline.details.filename', 'Filename')}>{job.filename}</Descriptions.Item>
              <Descriptions.Item label={getString('pipeline.details.status', 'Status')}>
                <Tag icon={statusDisplay.icon} color={statusDisplay.color}>
                  {statusDisplay.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={getString('pipeline.details.progress', 'Progress')}>
                {isProcessing ? `${Math.round(job.progress || 0)}%` : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label={getString('pipeline.jobs.created', 'Created')}>
                {formatTimestamp(job.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {formatTimestamp(job.updated_at)}
              </Descriptions.Item>
              {job.completed_at && (
                <Descriptions.Item label="Completed">
                  {formatTimestamp(job.completed_at)}
                </Descriptions.Item>
              )}
              {job.error_message && (
                <Descriptions.Item label="Error" span={2}>
                  <Text type="danger">{job.error_message}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            {isProcessing && (
              <div style={{ marginTop: '1rem' }}>
                <ProgressDisplay 
                  jobId={jobId}
                  jobData={job}
                  size="default"
                  showText={true}
                  showIcon={true}
                  showDetails={true}
                  showSSEConnection={false} // Disabled since SSE is globally disabled
                  // useSSE will use global config (disabled)
                />
              </div>
            )}
          </Card>
        </Col>

        <Col span={6}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <Statistic 
                  title="Total Frames" 
                  value={job.total_frames || 0}
                  prefix={<PlayCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={24}>
              <Card>
                <Statistic 
                  title="Processed Frames" 
                  value={job.processed_frames || 0}
                  valueStyle={{ color: isCompleted ? '#52c41a' : '#1890ff' }}
                />
              </Card>
            </Col>
            {(job.frames_published !== undefined || job.extraction_status) && (
              <Col span={24}>
                <Card>
                  <Statistic 
                    title="Frames Published" 
                    value={job.frames_published || 0}
                    suffix={job.frames_published ? `/ ${job.total_frames || 0}` : ''}
                    valueStyle={{ 
                      color: job.extraction_status === 'completed' ? '#52c41a' : '#faad14' 
                    }}
                    prefix={
                      <Tooltip title={`Frame extraction status: ${job.extraction_status || 'extracting'}`}>
                        {job.extraction_status === 'completed' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                      </Tooltip>
                    }
                  />
                  {job.publishing_progress !== undefined && (
                    <div style={{ marginTop: '8px' }}>
                      <Progress 
                        percent={Math.round(job.publishing_progress || 0)}
                        size="small"
                        status={job.extraction_status === 'completed' ? 'success' : 'active'}
                        format={(percent) => `${percent}% published`}
                      />
                    </div>
                  )}
                </Card>
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      {/* Export-First Actions */}
      {isCompleted && (
        <Card 
          title={
            <Space>
              <ExportOutlined />
              <Text strong>Data Export & Analysis Tools</Text>
              <Text type="secondary">({filteredResults.length} of {results.length} results)</Text>
            </Space>
          }
          style={{ marginBottom: '2rem' }}
        >
          {/* Export Preparation Message */}
          {job?.export_ready === false && job?.export_preparation_progress !== undefined && (
            <Alert
              type="info"
              message="Preparing Export Data"
              description={
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text>
                    Analysis complete! Preparing your export data...
                  </Text>
                  <Progress 
                    percent={Math.round(job.export_preparation_progress)} 
                    status="active"
                    format={(percent) => `${percent}% ready`}
                  />
                  <Text type="secondary">
                    This typically takes 10-30 seconds. The export button will become available when ready.
                  </Text>
                </Space>
              }
              showIcon
              style={{ marginBottom: '1rem' }}
            />
          )}

          <Row gutter={16}>
            <Col span={16}>
              <Space wrap>
                <Tooltip 
                  title={job?.export_ready === false ? 'Export data is being prepared. Please wait...' : ''}
                >
                  <Button 
                    type="primary" 
                    size="large"
                    icon={<FileExcelOutlined />}
                    loading={exportLoading}
                    disabled={job?.export_ready === false}
                    onClick={() => handleExport('csv')}
                  >
                    Export as CSV
                  </Button>
                </Tooltip>
                
                <Text type="secondary">
                  CSV export includes full blendshapes & emotions data
                </Text>
              </Space>
            </Col>
            <Col span={8} style={{ textAlign: 'right' }}>
              <Space>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    refetchJob();
                    refetchResults();
                  }}
                  loading={jobLoading}
                >
                  Refresh
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* Enhanced Results Interface - COMMENTED OUT FOR FUTURE IMPLEMENTATION */}
      {/* TODO: Re-enable results view after pagination integration is complete */}
    </div>
  );
};

export default JobDetails; 